import { defineStore } from "pinia";
import { ref, reactive } from "vue";
import svc from "@/services/accounting/budget.service";
import * as Toast from "@/utils/toast";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

const notifyError = (msg) => {
  if (Toast?.toast?.error) return Toast.toast.error(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "error");
  if (Toast?.error) return Toast.error(msg);
  if (typeof Toast === "function") return Toast(msg, "error");
  console.error(msg);
};

const notifySuccess = (msg) => {
  if (Toast?.toast?.success) return Toast.toast.success(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "success");
  if (Toast?.success) return Toast.success(msg);
  if (typeof Toast === "function") return Toast(msg, "success");
  console.log(msg);
};

export const useBudgetStore = defineStore("budget", () => {
  const items = ref([]);
  const current = ref(null);
  const loading = ref(false);
  const loading1 = ref(false);
  const error = ref(null);

  const meta = ref({
    total: 0,
    per_page: 25,
    current_page: 1,
    last_page: 1,
  });

  const filters = reactive({
    q: "",
    budget_type: "",
    from_date: "",
    to_date: "",
  });

  async function fetch(params = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.FA_ACCOUNTING))) {
      items.value = [];
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      const res = await svc.fetch({
        ...filters,
        ...params,
        page: params.page || meta.value.current_page,
      });

      items.value = res.data.data || [];
      if (res.data.meta) {
        meta.value = res.data.meta;
      }
    } catch (e) {
      error.value = e;
      console.error("Failed to load budgets", e);
    } finally {
      loading.value = false;
    }
  }

  async function fetchOne(id) {
    loading1.value = true;
    error.value = null;

    try {
      const res = await svc.show(id);
      current.value = res.data.data;
      return current.value;
    } catch (e) {
      error.value = e;
      console.error("Failed to load budget", e);
    } finally {
      loading1.value = false;
    }
  }

  async function create(payload) {
    loading.value = true;
    error.value = null;

    try {
      const res = await svc.create(payload);
      notifySuccess("Budget created");
      await fetch({ page: 1 });
      return res.data.data;
    } catch (e) {
      error.value = e;
      notifyError("Failed to create budget");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function update(id, payload) {
    loading.value = true;
    error.value = null;

    try {
      const res = await svc.update(id, payload);
      notifySuccess("Budget updated");
      await fetch();
      return res.data.data;
    } catch (e) {
      error.value = e;
      notifyError("Failed to update budget");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function remove(id) {
    loading.value = true;
    error.value = null;

    try {
      await svc.remove(id);
      notifySuccess("Budget deleted");
      await fetch();
    } catch (e) {
      error.value = e;
      notifyError("Failed to delete budget");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  return {
    items,
    current,
    loading,
    error,
    meta,
    filters,
    fetch,
    fetchOne,
    create,
    update,
    remove,
  };
});
